import React from 'react'
import { Typography, useTheme } from '@mui/material'
import { RoleManager } from '../../../../APIManager'
import { ButtonType } from '../../../../components/GenericButton/styles'
import DefaultModalActions from '../../../../components/GenericModal/DefaultModalActions'
import { notification } from '../../../../components/Notifications'
import { grey } from '../../../../colors'
import { type ApiError } from '../../../../models/Common'
import { type ViewerRoleRowData } from '../types'

interface DeleteViewerRoleProps {
  selectedRow?: ViewerRoleRowData
  handleCloseModal?: () => void
  showErrorModal?: () => void
  setErrorMessage?: (message: string) => void
  onDeleted?: (roleId: string) => void
}

const DeleteViewerRole: React.FC<DeleteViewerRoleProps> = ({
  selectedRow,
  handleCloseModal,
  showErrorModal,
  setErrorMessage,
  onDeleted
}) => {
  const theme = useTheme()
  const [isDeleting, setIsDeleting] = React.useState(false)

  const handleDelete = async (): Promise<void> => {
    if (!selectedRow?.roleId) return

    setIsDeleting(true)
    try {
      await RoleManager.deleteViewerCopy(selectedRow.roleId)
      handleCloseModal?.()
      onDeleted?.(selectedRow.roleId)
      notification('Viewer role deleted', { variant: 'success' })
    } catch (error) {
      const customError = error as ApiError
      if (customError?.status === 422) {
        setErrorMessage?.(customError.message)
        showErrorModal?.()
        return
      }
      notification(customError?.message ?? 'Failed to delete viewer role', { variant: 'error' })
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <>
      <Typography fontSize='14px' lineHeight={theme.spacing(5)} color={grey[850]}>
        This viewer role will be permanently deleted. You cannot delete a role while users are assigned to it.
      </Typography>
      <DefaultModalActions
        confirmButtonProps={{
          onClick: handleDelete,
          text: 'Delete',
          loading: isDeleting,
          buttonType: ButtonType.Destructive
        }}
        destructiveButtonProps={{
          onClick: handleCloseModal
        }}
      />
    </>
  )
}

export default DeleteViewerRole
